import type { LayerState } from '../types';

interface LayerEntry {
  row: HTMLLabelElement;
  checkbox: HTMLInputElement;
  state: HTMLSpanElement;
  detail: HTMLSpanElement;
}

const STATE_LABELS: Record<LayerState, string> = {
  loading: '読み込み中',
  ready: '表示中',
  fallback: '代替表示',
  error: '取得失敗',
  disabled: '未使用',
};

export class LayerPanel {
  private readonly root = document.getElementById('layerStatus');
  private readonly entries = new Map<string, LayerEntry>();
  private readonly toggleHandlers = new Map<string, (visible: boolean) => void>();

  add(
    id: string,
    label: string,
    onToggle: (visible: boolean) => void,
  ): void {
    if (!this.root) return;

    const row = document.createElement('label');
    row.className = 'layer-row';
    row.htmlFor = `layer-${id}`;

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = `layer-${id}`;
    checkbox.checked = true;
    checkbox.disabled = true;
    checkbox.addEventListener('change', () => {
      this.toggleHandlers.get(id)?.(checkbox.checked);
    });

    const copy = document.createElement('span');
    copy.className = 'layer-copy';
    const name = document.createElement('span');
    name.className = 'layer-name';
    name.textContent = label;
    const detail = document.createElement('span');
    detail.className = 'layer-detail';
    detail.textContent = STATE_LABELS.loading;
    copy.append(name, detail);

    const state = document.createElement('span');
    state.className = 'layer-state is-loading';
    state.setAttribute('aria-hidden', 'true');
    row.append(checkbox, copy, state);
    this.root.append(row);
    this.entries.set(id, { row, checkbox, state, detail });
    this.toggleHandlers.set(id, onToggle);
  }

  setToggleHandler(id: string, onToggle: (visible: boolean) => void): void {
    if (this.entries.has(id)) this.toggleHandlers.set(id, onToggle);
  }

  setAllEnabled(enabled: boolean): void {
    for (const entry of this.entries.values()) {
      if (entry.row.dataset.state === 'ready' || entry.row.dataset.state === 'fallback') {
        entry.checkbox.disabled = !enabled;
      }
    }
  }

  setStatus(id: string, state: LayerState, detail?: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.row.dataset.state = state;
    entry.state.className = `layer-state is-${state}`;
    entry.detail.textContent = detail ?? STATE_LABELS[state];
    entry.checkbox.disabled =
      state === 'loading' || state === 'error' || state === 'disabled';
    if (state === 'error' || state === 'disabled') entry.checkbox.checked = false;
  }
}
